import { useState } from 'react';
import { CALENDAR_NAME } from '@topspin/shared';
import { useMintCalendar, useStopCalendar, type SeasonView } from '../data.js';
import { buildSeasonIcs, calendarFileName, deliverIcs } from '../calendar.js';
import { getCalendarUrl } from '../storage.js';

/**
 * "Add to your calendar" (§7). Subscribing is the answer to what was actually asked — the season
 * dates barely move, but who is on court moves every week, and an event saying "you're playing"
 * on a night a sub took over is worse than no event. The download is here because one-tap
 * subscribing can still fail on a device we don't control, and a plan whose only path is the
 * fragile one leaves the player who asked with nothing.
 *
 * Which affordances show is driven by `calendarSubscription` off the server rather than by local
 * storage, so every device tells the truth about a subscription — including one it didn't mint,
 * and including whether it still covers every group this device holds (§7.1).
 */
export default function CalendarCard({
  view,
  playerId,
  online,
}: {
  view: SeasonView;
  playerId: string;
  online: boolean;
}) {
  const mint = useMintCalendar();
  const stop = useStopCalendar();
  const [copied, setCopied] = useState(false);
  const [stopped, setStopped] = useState(false);
  /** Set when a mint displaced an existing subscription — the old device's copy is now frozen. */
  const [replaced, setReplaced] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const subscription = view.calendarSubscription;
  // Only the device that minted it holds the URL: the server keeps the hash and cannot show the
  // link again, which is the same trade `mint-token` makes for a group ID.
  const stored = getCalendarUrl();
  const url = subscription ? stored : null;
  const key = view.groups[0]?.key;

  // Freezing a token's reach at mint time is what stops a feed widening under the player. The
  // honest price is telling them when their calendar is a night short, and offering the fix.
  const missing = subscription
    ? view.groups.filter((g) => !subscription.groupIds.includes(g.config.groupId))
    : [];
  const missingNames = missing.map((g) => g.config.name).join(' and ');

  if (!key) return null;
  const busy = mint.isPending || stop.isPending;

  /**
   * The link to hand a calendar app. Minting only when there isn't one already, so tapping
   * Apple and then Google doesn't revoke the link the first one is now polling.
   */
  async function resolveUrl(): Promise<string> {
    if (url) return url;
    const minted = await mint.mutateAsync({ key: key as string });
    if (subscription) setReplaced(true);
    setStopped(false);
    return minted.url;
  }

  /**
   * Navigation rather than an `<a href>`: the URL doesn't exist until the player asks for it,
   * and a link that first has to be minted can't be one. `assign` rather than `window.open`
   * because a popup opened after an await is what a popup blocker is for.
   */
  async function addTo(target: (url: string) => string) {
    try {
      window.location.assign(target(await resolveUrl()));
    } catch {
      // The mutation's own error state renders below; nothing useful to add here.
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(await resolveUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Same. */
    }
  }

  async function download() {
    setDownloadFailed(false);
    const ics = buildSeasonIcs(view, playerId);
    if (!(await deliverIcs(ics, calendarFileName(view.seasonId)))) setDownloadFailed(true);
  }

  function stopSubscription() {
    stop.mutate(
      { key: key as string },
      {
        onSuccess: () => {
          setStopped(true);
          setReplaced(false);
        },
      },
    );
  }

  return (
    <section className="card">
      <h2>Add to your calendar</h2>

      {stopped ? (
        <StoppedNotice />
      ) : (
        <>
          {subscription && missing.length > 0 ? (
            <p>
              Your calendar was set up before you added {missingNames}, so those nights aren't on
              it.
            </p>
          ) : subscription && !url ? (
            <p>
              Your calendar is set up on another device. Adding it here replaces that link — the
              nights already on the other phone will stop updating.
            </p>
          ) : (
            <p>
              Subscribe once and your tennis nights keep themselves up to date — subs included.
            </p>
          )}

          {replaced && <ReplacedNotice />}

          {/*
            One list answering *which calendar app is yours*. Apple first: it is what the player
            who asked for this is on, and what most of the group is on. The URL itself is never
            rendered — it is a bearer secret, and a settings screen is where people take
            screenshots and hand a phone across a table.
          */}
          <div className="actions calendar-actions">
            <button
              type="button"
              className="primary"
              disabled={!online || busy}
              onClick={() => void addTo((u) => u.replace(/^https?:/, 'webcal:'))}
            >
              {missing.length > 0 ? `Update to include ${missingNames}` : 'Add to Apple Calendar'}
            </button>
            {missing.length === 0 && (
              <>
                <button
                  type="button"
                  disabled={!online || busy}
                  onClick={() =>
                    void addTo(
                      (u) =>
                        `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(u)}`,
                    )
                  }
                >
                  Add to Google Calendar
                </button>
                <button
                  type="button"
                  disabled={!online || busy}
                  onClick={() =>
                    void addTo(
                      (u) =>
                        `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(u)}&name=Topspin`,
                    )
                  }
                >
                  Add to Outlook
                </button>
              </>
            )}
          </div>

          {/*
            Quiet and last: the three above are one list answering which calendar app is yours,
            and this answers the different question you only reach when none of them is. It is
            also the one path still standing the day a vendor changes its deep-link format.
          */}
          <p className="muted">
            Using something else?{' '}
            <button type="button" className="link" disabled={!online || busy} onClick={copyLink}>
              {copied ? 'Copied' : 'Copy the calendar link'}
            </button>
          </p>

          {/*
            Stated in the UI as well as in the plan: a subscribed calendar is not a notification
            channel. A sub arranged at 4pm for a 6:30 match will not be in anyone's calendar in
            time, and the one unset expectation that turns this feature into a complaint.
          */}
          <p className="muted">
            Calendar apps refresh on their own schedule, sometimes only once a day. Check Topspin
            for anything last-minute.
          </p>

          {!online && <p className="muted">You'll need a connection to set this up.</p>}
        </>
      )}

      {mint.isError && <p className="error">{(mint.error as Error).message}</p>}
      {stop.isError && <p className="error">{(stop.error as Error).message}</p>}

      <p className="muted calendar-footnote">
        <button type="button" className="link" onClick={() => void download()}>
          Download this season instead
        </button>
        {' — a snapshot that won’t update.'}
      </p>
      {downloadFailed && (
        <p className="error">This device wouldn't take the file. Try the calendar link instead.</p>
      )}

      {/*
        On its own line rather than trailing the download: sat next to “Download this season”,
        “this subscription” reads as pointing at the snapshot the sentence before it, which is
        the one thing here that cannot be stopped.
      */}
      {subscription && !stopped && (
        <p className="muted calendar-footnote">
          <button
            type="button"
            className="link"
            disabled={!online || busy}
            onClick={stopSubscription}
          >
            Stop this subscription
          </button>
        </p>
      )}
    </section>
  );
}

/**
 * Revoking the token is only half of it, and the half the app can do. Apple and Google keep a
 * subscribed calendar that starts failing — Apple with a notice people learn to dismiss, Google
 * usually silently — and the nights already downloaded stay on the phone forever, frozen at
 * whatever the lineup was that day. A player who taps Stop and does nothing else is left with a
 * permanent, wrong, silent copy of the season, which is worse than before they started because
 * it looks maintained.
 *
 * The calendar is named with the exact string the feed published, because that is what they are
 * looking for in their calendar list.
 */
function StoppedNotice() {
  return (
    <>
      <p>
        <strong>Subscription stopped.</strong>
      </p>
      <p>
        Now delete <strong>{CALENDAR_NAME}</strong> from your calendar app — it has stopped
        updating, but the nights already on it will stay there until you do.
      </p>
    </>
  );
}

/** Minting again kills the old link, so the other device is now holding the frozen copy. */
function ReplacedNotice() {
  return (
    <p>
      <strong>Your old calendar link has stopped working.</strong> On any other device it was set
      up on, delete <strong>{CALENDAR_NAME}</strong> from the calendar app — it has stopped
      updating, but the nights already on it will stay there until you do.
    </p>
  );
}
